import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors';
import type { Message } from '@/types/api';
import type { ChatMessage } from '@/types/chat';
import type { PersistedAssistantMetadata } from '@hector/shared-types/casediscovery';
import type { TimelineItem } from '../types/session.types';
import type { DisplayExchange } from '../types/displayExchange';
import { normalizeLegacyToolResult } from '../utils/normalizeLegacyToolResult';
import { buildTimelineFromToolResults } from '../utils/buildTimelineFromToolResults';
import { buildHistoricalExchanges } from '../utils/buildDisplayExchanges';

interface ConversationHistoryData {
  messages: ChatMessage[];
  toolTimelines: Map<string, TimelineItem[]>;
}

interface UseConversationHistoryResult {
  messages: ChatMessage[];
  historicalExchanges: DisplayExchange[];
  isLoading: boolean;
  /** True only after a successful REST read; skipped/new conversations stay false. */
  isHistoryReady: boolean;
  /**
   * Instant (ms epoch) de la dernière lecture REST réussie, `0` avant la première.
   * Le transcript s'en sert pour distinguer le cache d'une lecture fraîche (#1888).
   */
  historyFetchedAt: number;
}

/** Query key racine de l'historique v3 — exporté pour invalidation ciblée. */
export const V3_CONVERSATION_HISTORY_KEY = 'v3-conversation-history';

// Références stables pour les états vides : évite de recréer un objet à chaque
// render (sinon le useMemo de historicalExchanges churne inutilement).
const EMPTY_MESSAGES: ChatMessage[] = [];
const EMPTY_TIMELINES = new Map<string, TimelineItem[]>();
const EMPTY_HISTORY: ConversationHistoryData = {
  messages: EMPTY_MESSAGES,
  toolTimelines: EMPTY_TIMELINES,
};

/**
 * Fetch + normalisation de l'historique d'une conversation v3.
 *
 * WHY v3-specific path: the legacy `/conversations/:id/messages` reads the v1
 * `messages` table which is no longer populated. v3 persists into
 * `casediscovery_messages_v2` via `CaseDiscoveryMessageRepository`; this route
 * adapts those rows back into the chat wire shape.
 */
async function fetchConversationHistory(convId: string): Promise<ConversationHistoryData> {
  // `include=compaction_markers` : le backend n'émet le marqueur
  // `{ role: 'system', content: '', metadata: { kind: 'compaction_checkpoint' } }`
  // qu'aux clients qui le demandent. Un bundle plus ancien, encore ouvert quand le
  // backend est déployé, en ferait une bulle de réponse vide ; ce client-ci le
  // dessine en séparateur (`buildHistoricalExchanges`).
  const apiMessages = await apiClient.get<Message[]>(
    `/case-discovery-v3/conversations/${convId}/messages?include=compaction_markers`,
  );

  const chatMessages: ChatMessage[] = [];
  const timelines = new Map<string, TimelineItem[]>();

  for (const msg of apiMessages) {
    chatMessages.push({
      id: msg.id,
      conversationId: msg.conversation_id,
      role: msg.role,
      content: msg.content,
      // NOTE: citations and sources are returned by the backend but not declared
      // in the Message type (@/types/api.ts). Extend Message if these fields are
      // used elsewhere; for now access safely via metadata.
      citations: (msg as unknown as Record<string, unknown>).citations as ChatMessage['citations'],
      sources: (msg as unknown as Record<string, unknown>).sources as ChatMessage['sources'],
      metadata: msg.metadata,
      // Do not collapse absence into null: old persisted turns must keep their
      // conversation-level fallback, while null is a deliberate general turn.
      ...(msg.case_id !== undefined ? { caseId: msg.case_id } : {}),
      createdAt: new Date(msg.created_at),
      isStreaming: false,
      streamingComplete: true,
    });

    // Reconstruct the restored timeline (reasoning + tools) from persisted metadata.
    const metadata = msg.metadata as PersistedAssistantMetadata | undefined;
    if (msg.role === 'assistant') {
      const restored: TimelineItem[] = [];
      // Reasoning first — matches a live turn's flat order; `groupTimelineItems`
      // hoists it into the top ThinkingGroup regardless. Persisted in the DB
      // `reasoning` column, carried on the wire by `adaptHistoryToWire`.
      if (metadata?.reasoning) {
        restored.push({
          type: 'thinking',
          id: `restored-thinking-${msg.id}`,
          thinking: { content: metadata.reasoning, isActive: false, startedAt: 0 },
        });
      }
      if (metadata?.tool_results?.length) {
        // Normalize legacy format entries before reconstruction
        const normalized = metadata.tool_results.map(normalizeLegacyToolResult);
        restored.push(...buildTimelineFromToolResults(normalized));
      }
      if (restored.length > 0) {
        timelines.set(msg.id, restored);
      }
    }
  }

  return { messages: chatMessages, toolTimelines: timelines };
}

/**
 * Loads conversation history from DB and produces normalized DisplayExchange[].
 *
 * Backed by TanStack Query keyed on `conversationId` : ré-ouvrir une conversation
 * récemment consultée est instantané (cache, plus de trou blanc), et la clé par
 * conversation garantit qu'on n'affiche JAMAIS les messages d'une autre conv
 * pendant un chargement (pas de fuite cross-conversation/cabinet — cf. #238).
 * C'est pour cette raison qu'on n'utilise PAS `keepPreviousData` ici.
 *
 * @param conversationId - The conversation to load, or null to clear state
 * @param options.skipConversationId - Conversation just created locally, skip DB load
 */
export function useConversationHistory(
  conversationId: string | null,
  options?: { skipConversationId?: string | null },
): UseConversationHistoryResult {
  // Conversation créée localement à l'instant : ses messages ne sont pas encore
  // persistés, inutile d'aller chercher en DB (404/empty).
  const skip = !!conversationId && options?.skipConversationId === conversationId;

  const queryFn = useCallback(async (): Promise<ConversationHistoryData> => {
    try {
      return await fetchConversationHistory(conversationId as string);
    } catch (error) {
      const statusCode = error instanceof Error && 'statusCode' in error
        ? (error as { statusCode?: number }).statusCode
        : undefined;
      // The WS confirms a new conversation independently from the REST replica.
      // Between that event and the first visible row, a 404 means "no persisted
      // history yet" — not a retryable failure. Retrying it used to remount the
      // chat repeatedly during a parallel Canvas transition.
      if (statusCode === 404) return EMPTY_HISTORY;
      ErrorService.handle(error, { context: { action: 'load_conversation_history' }, silent: true });
      throw error;
    }
  }, [conversationId]);

  const query = useQuery<ConversationHistoryData>({
    queryKey: [V3_CONVERSATION_HISTORY_KEY, conversationId],
    queryFn,
    enabled: !!conversationId && !skip,
    staleTime: 60_000, // historique stable une fois chargé ; agent_done passe par le transcript local
    gcTime: 5 * 60_000,
  });

  const messages = query.data?.messages ?? EMPTY_MESSAGES;
  const toolTimelines = query.data?.toolTimelines ?? EMPTY_TIMELINES;

  const historicalExchanges = useMemo(
    () => buildHistoricalExchanges(messages, toolTimelines),
    [messages, toolTimelines],
  );

  return {
    messages,
    historicalExchanges,
    // `isLoading` (v5) = pending && fetching : true seulement au tout premier
    // chargement sans donnée en cache. Re-ouverture depuis le cache = false → pas
    // de flash de l'état de chargement.
    isLoading: query.isLoading && !skip,
    isHistoryReady: query.isSuccess,
    historyFetchedAt: query.dataUpdatedAt,
  };
}
